import React, { useMemo } from 'react';

import DepositInvoice from './DepositInvoice';
import LabelledCheckbox from './LabelledCheckbox';
import AvalaraOrderTaxButton from './AvalaraOrderTaxButton';
import SplitShipOrderTaxes from './SplitShipOrderTaxes';

import { formatMoney } from '../utils';
import { isAvalaraOrder } from '../helpers/order';
import SalesOrderUpfrontPaymentSettings from './SalesOrderUpfrontPaymentSettings';
import { useSelector } from 'react-redux';
import { selectSplitShip } from "../redux/splitShip";
import { Status } from '../constants';

const OrderSummary = ({ order, project, onUpdateOrder, onAddDepositInvoice, onUpdateDepositInvoice, onDeleteDepositInvoice, deposit_invoice, identity, credit_card_payable, stripe_cc_payable, stripe_ach_payable, forward_payment_cc, forward_payment_ach }) => {
  const locked = 1 == order.locked;

  // When avalara needs recalculation due to item update
  // we should not show any tax amount
  // as the total is already reset
  // And the backend tax amounts are also cleared
  const splitShip = useSelector(selectSplitShip);
  const orders = useSelector(state => state.entities.orders);

  // check if this sales order has an invoice already
  const hasInvoice = useMemo(() => {
      return Object.values(orders ?? {}).some(
        o =>
          o?.order_type === 'INVOICE' &&
          o?.parent_order_id === order.order_id
      );
  }, [orders, order.order_id]);

  const showTaxAmounts = splitShip?.status_id == Status.SPLIT_SHIP_COMPLETED || !isAvalaraOrder(order) || order.avalara_status !== "";
  const showSplitship = isAvalaraOrder(order) && splitShip && splitShip.active === 1;

  // when toggling splitship button on, it will try to disable upfront payment if it is type INVOICE (full)
  // however, it will fail in server side if there is already an invoice created for the sales order.
  // Just disable the splitship button to prevent confusion
  const disableSplitShip = locked || (hasInvoice && order.upfront_payment === 'INVOICE');

  const salesOrderUpfrontPaymentSettings = <tr><td colSpan={2}> <SalesOrderUpfrontPaymentSettings
    onUpdateDepositInvoice={onUpdateDepositInvoice}
    onDeleteDepositInvoice={onDeleteDepositInvoice}
    onAddDepositInvoice={onAddDepositInvoice}
    project={project}
    order={order}
    credit_card_payable={credit_card_payable}
    stripe_ach_payable={stripe_ach_payable}
    stripe_cc_payable={stripe_cc_payable}
    forward_payment_cc={forward_payment_cc}
    forward_payment_ach={forward_payment_ach}
    deposit_invoice={deposit_invoice}
    showSplitship={showSplitship}
  /></td></tr>;

  const avalaraOrderTaxButton = <AvalaraOrderTaxButton order={order} showSplitship={showSplitship} disableSplitShip={disableSplitShip} />;
  let taxArea = null;

  if (showSplitship) {
    taxArea = <SplitShipOrderTaxes
      order={order}
      splitShip={splitShip}
      showTaxAmounts={showTaxAmounts}
      onUpdateOrder={onUpdateOrder}
      avalaraOrderTaxButton={avalaraOrderTaxButton}
    />;
  } else {
    taxArea = <>
        {showTaxAmounts && order.tax_amounts.map((ta, i) => <tr key={i}>
          <td>{ta.label}</td>
          <td>${formatMoney(ta.amount)}</td>
        </tr>)}
        {isAvalaraOrder(order) && avalaraOrderTaxButton}
      </>;
  }

  return (
    <div className="large-4 column" style={{ padding: '2px' }}>
      <table className="summary">
        <tbody>
          <tr>
            <td>Subtotal</td>
            <td>${formatMoney(order.total_subtotal)}</td>
          </tr>
          { taxArea }
          {order.order_type === 'INVOICE' ?
            order.items.filter(i => i.parent_type === 'SYSTEM').map((item, i) => <tr key={i}>
              <td>{item.item_name}</td>
              <td>${formatMoney(item.total_total)}</td>
            </tr>) : null}
          {!showSplitship && <tr>
            <td>Total</td>
            <td>${formatMoney(order.total_total)}</td>
          </tr>}
          {!showSplitship && order.order_type === 'ESTIMATE' ?
            <tr>
              <td>
                <LabelledCheckbox label="Hide Totals" disabled={locked} value={order.hide_totals} onChange={onUpdateOrder(order.order_id, 'hide_totals', order.hide_totals)} />
              </td>
              <td>&nbsp;</td>
            </tr>
            : null}
          {order.order_type === 'SALES ORDER' ?
            salesOrderUpfrontPaymentSettings
            : null}
        </tbody>
      </table>
    </div>
  );
};

export default OrderSummary;
